import { useRef } from "react";
import { Camera, Images } from "lucide-react";

// EVERY place a photo is added offers BOTH sources, from this one file (gate: test_every_photo_button_offers_both).
// Camera = `capture` and NO `multiple` (Android ignores capture when multiple is set); gallery = no capture, many at once.
export const PHOTO_ACCEPT = "image/*,.heic,.heif";
export const TAKE_PHOTO = "Take photo";
export const CHOOSE_GALLERY = "Choose from gallery";

export function usePhotoSources({ onFiles, multiple = true, disabled = false, testidPrefix }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const pick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length) onFiles(files);
  };
  const inputs = (
    <>
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" disabled={disabled}
        data-testid={`${testidPrefix}-camera-input`} onChange={pick} />
      <input ref={galleryRef} type="file" accept={PHOTO_ACCEPT} multiple={multiple} className="hidden" disabled={disabled}
        data-testid={`${testidPrefix}-library-input`} onChange={pick} />
    </>
  );
  return { inputs, openCamera: () => cameraRef.current?.click(), openGallery: () => galleryRef.current?.click() };
}

const LOOKS = {
  primary: {
    cam: "h-10 px-4 text-sm font-semibold rounded-lg border-[#B01E28] bg-[#B01E28] text-white hover:bg-[#8f1820]",
    gal: "h-10 px-3 text-sm font-semibold rounded-lg border-slate-300 bg-white text-slate-700 hover:border-slate-500",
    icon: "w-4 h-4",
  },
  compact: {
    cam: "h-7 px-2 text-xs rounded-md border-slate-200 bg-white hover:border-slate-400",
    gal: "h-7 px-2 text-xs rounded-md border-slate-200 bg-white hover:border-slate-400",
    icon: "w-3.5 h-3.5",
  },
  md: {
    cam: "h-9 px-3 text-sm rounded-lg border-slate-300 bg-white hover:border-slate-500",
    gal: "h-9 px-3 text-sm rounded-lg border-slate-300 bg-white hover:border-slate-500",
    icon: "w-4 h-4",
  },
};

export function PhotoSourceButtons({ onFiles, multiple = true, disabled = false, testidPrefix, look = "compact",
  labelClass = "", dragging = false }) {
  const { inputs, openCamera, openGallery } = usePhotoSources({ onFiles, multiple, disabled, testidPrefix });
  const L = LOOKS[look] || LOOKS.compact;
  const ring = dragging ? " ring-2 ring-[#B01E28]/30" : "";
  const btn = "inline-flex items-center gap-1.5 border disabled:opacity-50 transition-colors";
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5" data-testid={`${testidPrefix}-sources`}>
      {inputs}
      <button type="button" disabled={disabled} onClick={openCamera} data-testid={`${testidPrefix}-camera-trigger`}
        className={`${btn} ${L.cam}${ring}`} title="Open the camera and take a new photo" aria-label={TAKE_PHOTO}>
        <Camera className={L.icon} /><span className={labelClass}>{dragging ? "Drop to upload" : TAKE_PHOTO}</span>
      </button>
      <button type="button" disabled={disabled} onClick={openGallery} data-testid={`${testidPrefix}-library-trigger`}
        className={`${btn} ${L.gal}${ring}`} title="Pick photos already on this device (or drag & drop)" aria-label={CHOOSE_GALLERY}>
        <Images className={L.icon} /><span className={labelClass}>{CHOOSE_GALLERY}</span>
      </button>
    </span>
  );
}
